import { useCallback, useEffect, useState } from "react";
import { CountrySender, CountrySenderStatus } from "../../../types/features/administration/CountrySender";
import { countrySenderService } from "../../../services/administration/countrySenderService";
import useToken from "../../auth/useToken";

export default function useCountrySenders() {
  const [countrySenders, setCountrySenders] = useState<CountrySender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const { token } = useToken();

  const getCountrySenders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await countrySenderService.getCountrySenders(token, 0, 100, true);
      setCountrySenders(data);
      setError(undefined);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createCountrySender = async (data: {
    country_id: number;
    status: CountrySenderStatus;
    default_daily_limit?: number | null;
  }) => {
    await countrySenderService.createCountrySender(token, data);
    await getCountrySenders();
  };

  const updateCountrySender = async (
    senderId: number,
    data: { status?: CountrySenderStatus; default_daily_limit?: number | null }
  ) => {
    await countrySenderService.updateCountrySender(token, senderId, data);
    await getCountrySenders();
  };

  const deleteCountrySender = async (senderId: number) => {
    await countrySenderService.deleteCountrySender(token, senderId);
    await getCountrySenders();
  };

  const addDestination = async (
    senderId: number,
    data: { country_destination_id: number; status: CountrySenderStatus }
  ) => {
    await countrySenderService.addDestination(token, senderId, data);
    await getCountrySenders();
  };

  const updateDestination = async (destinationId: number, data: { status: CountrySenderStatus }) => {
    await countrySenderService.updateDestination(token, destinationId, data);
    await getCountrySenders();
  };

  const deleteDestination = async (destinationId: number) => {
    await countrySenderService.deleteDestination(token, destinationId);
    await getCountrySenders();
  };

  useEffect(() => {
    getCountrySenders();
  }, [getCountrySenders]);

  return {
    countrySenders,
    countrySendersLoading: loading,
    countrySendersError: error,
    getCountrySenders,
    createCountrySender,
    updateCountrySender,
    deleteCountrySender,
    addDestination,
    updateDestination,
    deleteDestination,
  };
}
